import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { UnsavedChangesDialog } from '../../../pages/embeddings/components/UnsavedChangesDialog';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  RemoteDataCollectionConfig,
  RemoteDataCollectionConfigUpdateRequest,
  RemoteDataCollectionDefaults,
  RemoteDataCollectionDefaultsUpdateRequest,
  RemoteDataCollectionUserDefaults,
} from '../../types';
import {
  REMOTE_DC_DEFAULT_CARTON_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_DELTA_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_DELTA_THRESHOLD_PCT,
  REMOTE_DC_DEFAULT_DETECTED_POLYGON_NORMALIZATION_PCT,
  REMOTE_DC_DEFAULT_INCLUDE_DELTA_PRIORITY,
  REMOTE_DC_DEFAULT_INCLUDE_SIMILARITY_PRIORITY,
  REMOTE_DC_DEFAULT_LOW_EMBEDDING_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_LOW_EMBEDDING_THRESHOLD,
  REMOTE_DC_DEFAULT_MERGE_LINKED_CANDIDATES,
  REMOTE_DC_DEFAULT_PRODUCT_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_SAMPLE_SIZE,
  REMOTE_DC_DEFAULT_SEARCH_SIZE,
  REMOTE_DC_DEFAULT_SIMILARITY_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_SIMILARITY_THRESHOLD_PCT,
  REMOTE_DC_DEFAULT_SSM_CONFIDENCE_THRESHOLD_PCT,
  REMOTE_DC_MAX_SAMPLE_SIZE,
  REMOTE_DC_MAX_SEARCH_SIZE,
} from '../../utils/remoteDataCollectionDefaults';

const actionButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-secondary)',
  borderRadius: 6,
  height: 32,
  padding: '0 12px',
  fontSize: 12,
  fontWeight: 800,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const profileSectionStyle: React.CSSProperties = {
  borderTop: '1px solid var(--divider)',
  paddingTop: 'var(--forge-page-stack-gap)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--forge-page-stack-gap)',
};

const profileGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 'var(--forge-page-stack-gap)',
  alignItems: 'end',
};

const textInputStyle: React.CSSProperties = {
  minWidth: 220,
  height: 32,
  borderRadius: 6,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  padding: '0 10px',
  fontSize: 12,
  fontWeight: 700,
};

function clampRangeValue(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function rangeStepPrecision(step: number): number {
  return String(step).split('.')[1]?.length ?? 0;
}

function snapRangeValue(value: number, min: number, max: number, step: number): number {
  const clampedValue = clampRangeValue(value, min, max);
  const steppedValue = min + Math.round((clampedValue - min) / step) * step;
  return Number(clampRangeValue(steppedValue, min, max).toFixed(rangeStepPrecision(step)));
}

function isRangeValueStepAligned(value: number, min: number, max: number, step: number): boolean {
  const clampedValue = clampRangeValue(value, min, max);
  return Math.abs(snapRangeValue(value, min, max, step) - clampedValue) < 1e-9;
}

function parseRangeDraftValue(value: string): number | null {
  const trimmedValue = value.trim();
  if (
    !trimmedValue ||
    trimmedValue === '-' ||
    trimmedValue === '+' ||
    trimmedValue === '.' ||
    trimmedValue === '-.' ||
    trimmedValue === '+.' ||
    trimmedValue.endsWith('.')
  ) {
    return null;
  }
  const nextValue = Number(trimmedValue);
  return Number.isFinite(nextValue) ? nextValue : null;
}

function defaultDefaults(): RemoteDataCollectionDefaults {
  return {
    searchSize: REMOTE_DC_DEFAULT_SEARCH_SIZE,
    sampleSize: REMOTE_DC_DEFAULT_SAMPLE_SIZE,
    similarityThreshold: REMOTE_DC_DEFAULT_SIMILARITY_THRESHOLD_PCT / 100,
    deltaThreshold: REMOTE_DC_DEFAULT_DELTA_THRESHOLD_PCT / 100,
    ssmConfidenceThreshold: REMOTE_DC_DEFAULT_SSM_CONFIDENCE_THRESHOLD_PCT / 100,
    mergeLinkedCandidates: REMOTE_DC_DEFAULT_MERGE_LINKED_CANDIDATES,
    lowEmbeddingThreshold: REMOTE_DC_DEFAULT_LOW_EMBEDDING_THRESHOLD,
    includeSimilarityPriority: REMOTE_DC_DEFAULT_INCLUDE_SIMILARITY_PRIORITY,
    includeDeltaPriority: REMOTE_DC_DEFAULT_INCLUDE_DELTA_PRIORITY,
    similarityPriorityWeight: REMOTE_DC_DEFAULT_SIMILARITY_PRIORITY_WEIGHT,
    deltaPriorityWeight: REMOTE_DC_DEFAULT_DELTA_PRIORITY_WEIGHT,
    lowEmbeddingPriorityWeight: REMOTE_DC_DEFAULT_LOW_EMBEDDING_PRIORITY_WEIGHT,
    productPriorityWeight: REMOTE_DC_DEFAULT_PRODUCT_PRIORITY_WEIGHT,
    cartonPriorityWeight: REMOTE_DC_DEFAULT_CARTON_PRIORITY_WEIGHT,
    detectedPolygonNormalization: REMOTE_DC_DEFAULT_DETECTED_POLYGON_NORMALIZATION_PCT / 100,
  };
}

function defaultConfig(): RemoteDataCollectionConfig {
  return {
    adminDefaults: defaultDefaults(),
    nonAdminDefaults: defaultDefaults(),
    userDefaults: [],
  };
}

function toRequest(
  config: RemoteDataCollectionDefaults
): RemoteDataCollectionDefaultsUpdateRequest {
  return {
    search_size: config.searchSize,
    sample_size: config.sampleSize,
    similarity_threshold: config.similarityThreshold,
    delta_threshold: config.deltaThreshold,
    ssm_confidence_threshold: config.ssmConfidenceThreshold,
    merge_linked_candidates: config.mergeLinkedCandidates,
    low_embedding_threshold: config.lowEmbeddingThreshold,
    include_similarity_priority: config.includeSimilarityPriority,
    include_delta_priority: config.includeDeltaPriority,
    similarity_priority_weight: config.similarityPriorityWeight,
    delta_priority_weight: config.deltaPriorityWeight,
    low_embedding_priority_weight: config.lowEmbeddingPriorityWeight,
    product_priority_weight: config.productPriorityWeight,
    carton_priority_weight: config.cartonPriorityWeight,
    detected_polygon_normalization: config.detectedPolygonNormalization,
  };
}

function toConfigRequest(
  config: RemoteDataCollectionConfig
): RemoteDataCollectionConfigUpdateRequest {
  const seenUserKeys = new Set<string>();
  return {
    admin_defaults: toRequest(config.adminDefaults),
    non_admin_defaults: toRequest(config.nonAdminDefaults),
    user_defaults: config.userDefaults
      .map((userDefault) => ({
        user_key: userDefault.userKey.trim().toLowerCase(),
        ...toRequest(userDefault),
      }))
      .filter((userDefault) => {
        if (!userDefault.user_key || seenUserKeys.has(userDefault.user_key)) {
          return false;
        }
        seenUserKeys.add(userDefault.user_key);
        return true;
      }),
  };
}

function editableDefaults(config: RemoteDataCollectionDefaults) {
  return {
    searchSize: config.searchSize,
    sampleSize: config.sampleSize,
    similarityThreshold: config.similarityThreshold,
    deltaThreshold: config.deltaThreshold,
    ssmConfidenceThreshold: config.ssmConfidenceThreshold,
    mergeLinkedCandidates: config.mergeLinkedCandidates,
    lowEmbeddingThreshold: config.lowEmbeddingThreshold,
    includeSimilarityPriority: config.includeSimilarityPriority,
    includeDeltaPriority: config.includeDeltaPriority,
    similarityPriorityWeight: config.similarityPriorityWeight,
    deltaPriorityWeight: config.deltaPriorityWeight,
    lowEmbeddingPriorityWeight: config.lowEmbeddingPriorityWeight,
    productPriorityWeight: config.productPriorityWeight,
    cartonPriorityWeight: config.cartonPriorityWeight,
    detectedPolygonNormalization: config.detectedPolygonNormalization,
  };
}

function configSignature(config: RemoteDataCollectionConfig): string {
  return JSON.stringify({
    adminDefaults: editableDefaults(config.adminDefaults),
    nonAdminDefaults: editableDefaults(config.nonAdminDefaults),
    userDefaults: config.userDefaults.map((userDefault) => ({
      userKey: userDefault.userKey,
      ...editableDefaults(userDefault),
    })),
  });
}

function formatUpdatedAt(value?: string | null): string {
  if (!value) return 'Never saved';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never saved';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function updatedByEmail(value?: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed && trimmed.includes('@') ? trimmed : null;
}

function updatedText(config: RemoteDataCollectionDefaults): string {
  const email = updatedByEmail(config.updatedBy);
  return `Updated ${formatUpdatedAt(config.updatedAtUtc)}${email ? ` by ${email}` : ''}`;
}

type DefaultsPatch = Partial<RemoteDataCollectionDefaults>;

interface ProfileEditorProps {
  title: string;
  idPrefix: string;
  subtitle?: string;
  config: RemoteDataCollectionDefaults;
  onChange: (patch: DefaultsPatch) => void;
  headerSlot?: React.ReactNode;
}

function RangeControl({
  label,
  idPrefix,
  value,
  min,
  max,
  step = 1,
  unit = '',
  headerSlot,
  onChange,
}: {
  label: string;
  idPrefix: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  headerSlot?: React.ReactNode;
  onChange: (value: number) => void;
}) {
  const [draftValue, setDraftValue] = useState(String(value));
  const controlId = `rdc-config-${idPrefix}-${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')}-${min}-${max}`;

  useEffect(() => {
    setDraftValue(String(value));
  }, [value]);

  const commitDraftValue = useCallback(() => {
    const parsedValue = parseRangeDraftValue(draftValue);
    if (parsedValue === null) {
      setDraftValue(String(value));
      return;
    }
    const nextValue = snapRangeValue(parsedValue, min, max, step);
    setDraftValue(String(nextValue));
    onChange(nextValue);
  }, [draftValue, max, min, onChange, step, value]);

  const handleDraftValueChange = useCallback(
    (nextDraftValue: string) => {
      setDraftValue(nextDraftValue);
      const parsedValue = parseRangeDraftValue(nextDraftValue);
      if (parsedValue !== null && isRangeValueStepAligned(parsedValue, min, max, step)) {
        onChange(snapRangeValue(parsedValue, min, max, step));
      }
    },
    [max, min, onChange, step]
  );

  return (
    <div className="mdc-range-control rdc-dashboard-range-control">
      <div className="mdc-range-header">
        <label className="mdc-range-label" htmlFor={controlId}>
          {label}
        </label>
        <span className="rdc-dashboard-range-header-actions">
          {headerSlot}
          <span className="mdc-value-pill rdc-range-value-editor">
            <input
              aria-label={`${label} value`}
              className="rdc-range-value-input"
              type="number"
              min={min}
              max={max}
              step={step}
              value={draftValue}
              onChange={(event) => handleDraftValueChange(event.target.value)}
              onBlur={commitDraftValue}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitDraftValue();
                }
              }}
            />
            {unit ? <span className="rdc-range-value-unit">{unit}</span> : null}
          </span>
        </span>
      </div>
      <input
        id={controlId}
        className="mdc-range-input"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </div>
  );
}

function ToggleControl({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="mdc-range-control mdc-range-control--toggle-only">
      <label className="mdc-range-toggle">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
        />
        {label}
      </label>
    </div>
  );
}

function InlinePriorityToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="mdc-range-toggle rdc-dashboard-inline-priority-toggle">
      <input
        type="checkbox"
        aria-label={label}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      Priority
    </label>
  );
}

function ProfileEditor({
  title,
  idPrefix,
  subtitle,
  config,
  onChange,
  headerSlot,
}: ProfileEditorProps) {
  return (
    <section style={profileSectionStyle}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div className="remote-dc-profile-title">{title}</div>
          {subtitle ? (
            <div style={{ color: 'var(--text-tertiary)', fontSize: 11, marginTop: 4 }}>
              {subtitle}
            </div>
          ) : null}
        </div>
        {headerSlot}
      </div>

      <div style={profileGridStyle}>
        <RangeControl
          label="Searched"
          idPrefix={idPrefix}
          value={config.searchSize}
          min={1}
          max={REMOTE_DC_MAX_SEARCH_SIZE}
          step={1}
          onChange={(searchSize) => onChange({ searchSize })}
        />
        <RangeControl
          label="Tasks"
          idPrefix={idPrefix}
          value={config.sampleSize}
          min={1}
          max={REMOTE_DC_MAX_SAMPLE_SIZE}
          step={1}
          onChange={(sampleSize) => onChange({ sampleSize })}
        />
        <RangeControl
          label="Sim <"
          idPrefix={idPrefix}
          value={Math.round(config.similarityThreshold * 100)}
          min={0}
          max={100}
          unit="%"
          headerSlot={
            <InlinePriorityToggle
              label="Sim priority"
              checked={config.includeSimilarityPriority}
              onChange={(includeSimilarityPriority) => onChange({ includeSimilarityPriority })}
            />
          }
          onChange={(value) => onChange({ similarityThreshold: value / 100 })}
        />
        <RangeControl
          label="Delta <"
          idPrefix={idPrefix}
          value={Math.round(config.deltaThreshold * 100)}
          min={0}
          max={100}
          unit="%"
          headerSlot={
            <InlinePriorityToggle
              label="Delta priority"
              checked={config.includeDeltaPriority}
              onChange={(includeDeltaPriority) => onChange({ includeDeltaPriority })}
            />
          }
          onChange={(value) => onChange({ deltaThreshold: value / 100 })}
        />
        <RangeControl
          label="SSM >="
          idPrefix={idPrefix}
          value={Math.round(config.ssmConfidenceThreshold * 100)}
          min={0}
          max={100}
          unit="%"
          onChange={(value) => onChange({ ssmConfidenceThreshold: value / 100 })}
        />
        <RangeControl
          label="# Emb <="
          idPrefix={idPrefix}
          value={config.lowEmbeddingThreshold}
          min={0}
          max={20}
          step={1}
          onChange={(lowEmbeddingThreshold) => onChange({ lowEmbeddingThreshold })}
        />
        <RangeControl
          label="Sim weight"
          idPrefix={idPrefix}
          value={config.similarityPriorityWeight}
          min={0}
          max={5}
          step={0.25}
          unit="x"
          onChange={(similarityPriorityWeight) => onChange({ similarityPriorityWeight })}
        />
        <RangeControl
          label="Delta weight"
          idPrefix={idPrefix}
          value={config.deltaPriorityWeight}
          min={0}
          max={5}
          step={0.25}
          unit="x"
          onChange={(deltaPriorityWeight) => onChange({ deltaPriorityWeight })}
        />
        <RangeControl
          label="Low emb weight"
          idPrefix={idPrefix}
          value={config.lowEmbeddingPriorityWeight}
          min={0}
          max={5}
          step={0.25}
          unit="x"
          onChange={(lowEmbeddingPriorityWeight) => onChange({ lowEmbeddingPriorityWeight })}
        />
        <RangeControl
          label="Product weight"
          idPrefix={idPrefix}
          value={config.productPriorityWeight}
          min={0}
          max={5}
          step={0.25}
          unit="x"
          onChange={(productPriorityWeight) => onChange({ productPriorityWeight })}
        />
        <RangeControl
          label="Carton weight"
          idPrefix={idPrefix}
          value={config.cartonPriorityWeight}
          min={0}
          max={5}
          step={0.25}
          unit="x"
          onChange={(cartonPriorityWeight) => onChange({ cartonPriorityWeight })}
        />
        <RangeControl
          label="Normalize"
          idPrefix={idPrefix}
          value={Math.round(config.detectedPolygonNormalization * 100)}
          min={0}
          max={100}
          step={5}
          unit="%"
          onChange={(value) => onChange({ detectedPolygonNormalization: value / 100 })}
        />
        <ToggleControl
          label="Merge grouped candidates"
          checked={config.mergeLinkedCandidates}
          onChange={(mergeLinkedCandidates) => onChange({ mergeLinkedCandidates })}
        />
      </div>

      <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>{updatedText(config)}</div>
    </section>
  );
}

export const RemoteDataCollectionConfiguration: React.FC = () => {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [config, setConfig] = useState<RemoteDataCollectionConfig>(defaultConfig);
  const [savedConfigSignature, setSavedConfigSignature] = useState(() =>
    configSignature(defaultConfig())
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const currentConfigSignature = useMemo(() => configSignature(config), [config]);
  const hasUnsavedChanges = currentConfigSignature !== savedConfigSignature;
  const allowNextNavigationRef = useRef(false);
  const shouldBlockNavigation = useCallback(
    () => hasUnsavedChanges && !isSaving && !allowNextNavigationRef.current,
    [hasUnsavedChanges, isSaving]
  );
  const blocker = useBlocker(shouldBlockNavigation);

  const loadConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const loadedConfig = await ForgeApiService.fetchRemoteDataCollectionConfigDefaults(token);
      setConfig(loadedConfig);
      setSavedConfigSignature(configSignature(loadedConfig));
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Error loading Remote DC configuration'
      );
    } finally {
      setIsLoading(false);
    }
  }, [showErrorToast, token]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (!hasUnsavedChanges || isSaving) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isSaving]);

  useEffect(() => {
    if (!hasUnsavedChanges || isSaving) {
      return undefined;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target || anchor.hasAttribute('download')) {
        return;
      }

      const nextUrl = new URL(anchor.href, window.location.href);
      const currentUrl = new URL(window.location.href);
      if (nextUrl.origin !== currentUrl.origin) {
        return;
      }

      const nextPath = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      const currentPath = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
      if (nextPath === currentPath) {
        return;
      }

      if (
        !window.confirm('You have unsaved Remote DC configuration changes. Leave without saving?')
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      allowNextNavigationRef.current = true;
      window.setTimeout(() => {
        allowNextNavigationRef.current = false;
      }, 0);
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, [hasUnsavedChanges, isSaving]);

  const patchAdminDefaults = (patch: DefaultsPatch) => {
    setConfig((current) => ({
      ...current,
      adminDefaults: { ...current.adminDefaults, ...patch },
    }));
  };

  const patchNonAdminDefaults = (patch: DefaultsPatch) => {
    setConfig((current) => ({
      ...current,
      nonAdminDefaults: { ...current.nonAdminDefaults, ...patch },
    }));
  };

  const patchUserDefaults = (index: number, patch: DefaultsPatch | { userKey: string }) => {
    setConfig((current) => ({
      ...current,
      userDefaults: current.userDefaults.map((userDefault, currentIndex) =>
        currentIndex === index ? { ...userDefault, ...patch } : userDefault
      ),
    }));
  };

  const addUserDefaults = () => {
    setConfig((current) => ({
      ...current,
      userDefaults: [
        ...current.userDefaults,
        {
          userKey: '',
          ...current.nonAdminDefaults,
          updatedAtUtc: null,
          updatedBy: null,
        },
      ],
    }));
  };

  const removeUserDefaults = (index: number) => {
    setConfig((current) => ({
      ...current,
      userDefaults: current.userDefaults.filter((_, currentIndex) => currentIndex !== index),
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const saved = await ForgeApiService.updateRemoteDataCollectionConfigDefaults(
        token,
        toConfigRequest(config)
      );
      setConfig(saved);
      setSavedConfigSignature(configSignature(saved));
      showSuccessToast('Remote DC configuration saved');
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Error saving Remote DC configuration'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefresh = async () => {
    if (
      hasUnsavedChanges &&
      !window.confirm('Discard unsaved Remote DC configuration changes and reload?')
    ) {
      return;
    }
    await loadConfig();
  };

  return (
    <div className="page-content page-content--wide forge-page-stack">
      <div className="forge-card rdc-dashboard-hero">
        <div className="rdc-dashboard-title-block">
          <div>
            <div className="rdc-dashboard-title">Remote DC Configuration</div>
            <div className="rdc-dashboard-copy">
              These profiles control default Remote DC thresholds. Refreshes always use low
              similarity or low delta, and always sample the latest inferences.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {hasUnsavedChanges ? (
              <span style={{ color: 'var(--warning)', fontSize: 12, fontWeight: 800 }}>
                Unsaved changes
              </span>
            ) : null}
            <button
              type="button"
              style={{ ...actionButtonStyle, opacity: isLoading ? 0.65 : 1 }}
              disabled={isLoading}
              onClick={() => void handleRefresh()}
            >
              <RefreshOutlinedIcon style={{ fontSize: 15 }} />
              Refresh
            </button>
            {hasUnsavedChanges || isSaving ? (
              <button
                type="button"
                style={{
                  ...actionButtonStyle,
                  background: 'var(--accent)',
                  color: '#fff',
                  opacity: isSaving || isLoading ? 0.65 : 1,
                }}
                disabled={isSaving || isLoading}
                onClick={() => void handleSave()}
              >
                <SaveOutlinedIcon style={{ fontSize: 15 }} />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            ) : null}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--forge-page-stack-gap)',
            marginTop: 'var(--forge-page-stack-gap)',
          }}
        >
          <ProfileEditor
            title="Non-admin default"
            idPrefix="non-admin"
            subtitle="Used for labelers unless a user-specific override exists."
            config={config.nonAdminDefaults}
            onChange={patchNonAdminDefaults}
          />

          <ProfileEditor
            title="Admin default"
            idPrefix="admin"
            subtitle="Used for admins and Focal employees unless a user-specific override exists."
            config={config.adminDefaults}
            onChange={patchAdminDefaults}
          />

          <section style={profileSectionStyle}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div className="remote-dc-profile-title">User defaults</div>
                <div style={{ color: 'var(--text-tertiary)', fontSize: 11, marginTop: 4 }}>
                  Overrides are keyed by user email or actor key.
                </div>
              </div>
              <button
                type="button"
                style={{ ...actionButtonStyle, opacity: isLoading ? 0.65 : 1 }}
                disabled={isLoading}
                onClick={addUserDefaults}
              >
                <AddCircleOutlineIcon style={{ fontSize: 15 }} />
                Add user
              </button>
            </div>

            {config.userDefaults.length === 0 ? (
              <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
                {isLoading ? 'Loading user defaults...' : 'No user-specific defaults configured.'}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--forge-page-stack-gap)',
                }}
              >
                {config.userDefaults.map((userDefault: RemoteDataCollectionUserDefaults, index) => (
                  <ProfileEditor
                    key={`user-override-${index}`}
                    title={`User override ${index + 1}`}
                    idPrefix={`user-${index + 1}`}
                    subtitle="Specific defaults for this user."
                    config={userDefault}
                    onChange={(patch) => patchUserDefaults(index, patch)}
                    headerSlot={
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <input
                          aria-label={`User key ${index + 1}`}
                          value={userDefault.userKey}
                          placeholder="user@focal.systems"
                          style={textInputStyle}
                          onChange={(event) =>
                            patchUserDefaults(index, { userKey: event.target.value })
                          }
                        />
                        <button
                          type="button"
                          aria-label={`Remove user override ${index + 1}`}
                          style={{ ...actionButtonStyle, width: 34, padding: 0 }}
                          onClick={() => removeUserDefaults(index)}
                        >
                          <DeleteOutlineIcon style={{ fontSize: 16 }} />
                        </button>
                      </div>
                    }
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
      <UnsavedChangesDialog
        open={blocker.state === 'blocked'}
        onCancel={() => blocker.reset?.()}
        onDiscard={() => blocker.proceed?.()}
      />
    </div>
  );
};
